import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createVNode as _createVNode, withModifiers as _withModifiers, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "sticky top-0 z-10 w-full items-center bg-white" }
const _hoisted_3 = { class: "pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center px-2" }
const _hoisted_4 = {
  key: 1,
  class: "flex h-6 w-full items-center px-2"
}
const _hoisted_5 = { class: "truncate" }
const _hoisted_6 = {
  key: 0,
  class: "text-muted-foreground ml-auto text-[10px]"
}
const _hoisted_7 = { class: "absolute right-2 flex size-3.5 items-center justify-center" }
const _hoisted_8 = { class: "text-muted-foreground p-2 text-xs" }
const _hoisted_9 = { class: "text-muted-foreground p-2 text-xs" }

import { ChevronDown, Check, Search, CircleMinus, LoaderCircle } from 'lucide-vue-next';
    import {
        ComboboxAnchor,
        ComboboxContent,
        ComboboxInput,
        ComboboxItem,
        ComboboxItemIndicator,
        ComboboxVirtualizer,
        ComboboxEmpty,
        ComboboxRoot,
        ComboboxTrigger,
        ComboboxViewport,
        ComboboxLabel,
        ComboboxSeparator,
        useFilter,
        AcceptableValue,
    } from 'reka-ui';
    import { computed, ref } from 'vue';
    import { SelectOption } from '@/types';
    import { useI18n } from 'vue-i18n';
    import { Button } from '@/components/ui/button';
    import { cn } from '@/lib/utils';

    interface Props {
        options: SelectOption[] | Record<string, any>[];
        placeholder?: string;
        translate?: boolean;
        loading?: boolean;
        showAddon?: boolean;
        labelBy?: string;
        valueBy?: string;
        showRemoveButton?: boolean;
        contentClass?: string;
        position?: 'popper' | 'inline';
        side?: 'top' | 'bottom';
        disabled?: boolean;
    }

    
export default /*@__PURE__*/_defineComponent({
  __name: 'Combobox',
  props: /*@__PURE__*/_mergeModels({
    options: {},
    placeholder: {},
    translate: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    showAddon: { type: Boolean, default: true },
    labelBy: { default: 'label' },
    valueBy: { default: 'value' },
    showRemoveButton: { type: Boolean, default: true },
    contentClass: {},
    position: { default: 'popper' },
    side: { default: 'bottom' },
    disabled: { type: Boolean }
  }, {
    "modelValue": {},
    "modelModifiers": {},
  }),
  emits: ["update:modelValue"],
  setup(__props: any) {

    const props = __props;

    const model = _useModel<AcceptableValue>(__props, "modelValue");

    const { t } = useI18n();

    const searchTerm = ref('');
    const { contains } = useFilter({ sensitivity: 'base' });

    const getOptionValue = (option: SelectOption | Record<string, any>): string => {
        return (option as Record<string, any>)[props.valueBy];
    };

    const getOptionLabel = (option: SelectOption | Record<string, any>): string => {
        const label = (option as Record<string, any>)[props.labelBy];
        return props.translate ? t(label) : label;
    };

    const translatedOptions = computed(() =>
        props.options.map((option) => ({
            ...option,
            value: getOptionValue(option),
            label: getOptionLabel(option),
            addon: option.addon,
            is_group: option.is_group,
            is_seperator: option.is_seperator,
            disabled: option.disabled,
        })),
    );

    const filteredOptions = computed(() =>
        translatedOptions.value.filter(
            (option) =>
                option.is_group ||
                option.is_seperator ||
                contains(option.label, searchTerm.value) ||
                option.value === null,
        ),
    );

    const placeholderWithDefault = computed(
        () => props.placeholder || t('forms.select_placeholder'),
    );

    const selectedOption = computed(() => {
        return translatedOptions.value.find((opt) => opt.value === model.value);
    });

    const open = ref(false);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(ComboboxRoot), {
    modelValue: model.value,
    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((model).value = $event)),
    open: open.value,
    "onUpdate:open": _cache[4] || (_cache[4] = ($event: any) => ((open).value = $event)),
    multiple: false,
    class: "group relative",
    disabled: _ctx.disabled
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(ComboboxAnchor), { class: "group-data-[disabled]:bg-faded flex w-full gap-2" }, {
        default: _withCtx(() => [
          _createVNode(_unref(ComboboxTrigger), { "as-child": "" }, {
            default: _withCtx(() => [
              _createVNode(_unref(Button), {
                variant: "input",
                class: _normalizeClass([!selectedOption.value && 'text-faded-foreground', "flex-1 transition-opacity duration-0 disabled:opacity-100"]),
                size: "xs",
                disabled: _ctx.loading || _ctx.disabled
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(selectedOption.value ? selectedOption.value.label : placeholderWithDefault.value) + " ", 1),
                  (!_ctx.loading)
                    ? (_openBlock(), _createBlock(_unref(ChevronDown), {
                        key: 0,
                        class: "text-foreground/50 size-4 cursor-pointer"
                      }))
                    : (_openBlock(), _createBlock(_unref(LoaderCircle), {
                        key: 1,
                        class: "text-foreground/50 ml-auto size-4 animate-spin"
                      }))
                ]),
                _: 1
              }, 8, ["class", "disabled"])
            ]),
            _: 1
          }),
          (model.value && _ctx.showRemoveButton && !_ctx.disabled)
            ? (_openBlock(), _createBlock(_unref(Button), {
                key: 0,
                variant: "ghost_destructive",
                size: "xs",
                width: "narrow",
                class: "text-muted-foreground",
                onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (model.value = null), ["prevent"]))
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(CircleMinus))
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      _createVNode(_unref(ComboboxContent), {
        class: _normalizeClass(
                _unref(cn)(
                    'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 absolute z-50 mt-1 max-h-[200px] origin-top overflow-y-auto rounded-md border bg-white shadow-md will-change-[opacity,transform]',
                    _ctx.position === 'inline'
                        ? 'w-full'
                        : 'origin-[50% 0px] w-[var(--reka-popper-anchor-width)]',
                    _ctx.side === 'top' && 'translate-y-[calc(-100%-10px)]',
                    _ctx.contentClass,
                )
            ),
        onMousedown: _cache[2] || (_cache[2] = _withModifiers((event) => event.preventDefault(), ["stop"])),
        position: _ctx.position,
        "side-flip": "",
        align: "start",
        side: _ctx.side
      }, {
        default: _withCtx(() => [
          (!!_ctx.options.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                  _createVNode(_unref(ComboboxInput), {
                    modelValue: searchTerm.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchTerm).value = $event)),
                    "display-value": () => '',
                    class: "h-7.5 w-full rounded-none border-0 border-b pl-7.5 text-xs outline-none focus-visible:ring-0",
                    placeholder: _unref(t)('forms.search')
                  }, null, 8, ["modelValue", "placeholder"]),
                  _createElementVNode("span", _hoisted_3, [
                    _createVNode(_unref(Search), { class: "text-faded-foreground size-3.5" })
                  ])
                ]),
                _createVNode(_unref(ComboboxViewport), { class: "p-1" }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(ComboboxVirtualizer), {
                      options: filteredOptions.value,
                      "text-content": (opt) => opt.label,
                      "estimate-size": 24
                    }, {
                      default: _withCtx(({ option }) => [
                        (option.is_group)
                          ? (_openBlock(), _createBlock(_unref(ComboboxLabel), {
                              key: option.label,
                              class: "text-foreground/50 mx-2 w-full px-0 py-1.5 text-xs leading-none"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(option.label), 1)
                              ]),
                              _: 2
                            }, 1024))
                          : (option.is_seperator)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                (_openBlock(), _createBlock(_unref(ComboboxSeparator), {
                                  class: "bg-border h-[1px] w-full",
                                  key: `${option.value}`
                                }))
                              ]))
                            : (_openBlock(), _createBlock(_unref(ComboboxItem), {
                                key: 2,
                                class: "data-[state=checked]:text-primary text-foreground data-[disabled]:text-muted-foreground data-[highlighted]:bg-primary/10 relative flex w-full cursor-pointer items-center gap-2 rounded-sm py-1.5 pr-2 pl-2 text-xs leading-none select-none data-[disabled]:pointer-events-none data-[highlighted]:outline-none data-[state=checked]:pr-8",
                                value: option.value,
                                disabled: option.disabled
                              }, {
                                default: _withCtx(() => [
                                  _createElementVNode("span", _hoisted_5, _toDisplayString(option.label), 1),
                                  (_ctx.showAddon && option.addon)
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(option.addon), 1))
                                    : _createCommentVNode("", true),
                                  _createElementVNode("span", _hoisted_7, [
                                    _createVNode(_unref(ComboboxItemIndicator), null, {
                                      default: _withCtx(() => [
                                        _createVNode(_unref(Check), { class: "text-primary size-3" })
                                      ]),
                                      _: 1
                                    })
                                  ])
                                ]),
                                _: 2
                              }, 1032, ["value", "disabled"]))
                      ]),
                      _: 1
                    }, 8, ["options", "text-content"]),
                    _createVNode(_unref(ComboboxEmpty), null, {
                      default: _withCtx(() => [
                        _createElementVNode("span", _hoisted_8, _toDisplayString(_unref(t)('forms.found_no_options')), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]))
            : (_openBlock(), _createBlock(_unref(ComboboxViewport), {
                key: 1,
                class: "p-1"
              }, {
                default: _withCtx(() => [
                  _createElementVNode("span", _hoisted_9, _toDisplayString(_ctx.loading ? _unref(t)('forms.loading') : _unref(t)('forms.no_options')), 1)
                ]),
                _: 1
              }))
        ]),
        _: 1
      }, 8, ["class", "position", "side"])
    ]),
    _: 1
  }, 8, ["modelValue", "open", "disabled"]))
}
}

})